'use client';

import uniq from 'lodash/uniq';
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { getLocationsData, StrapiCityDatum, StrapiCountries } from '@/apis/strapi';
import {
  bookTicket,
  commuterPassBookTicket,
  CommuterPass,
  commuterSearch,
  Fare,
  getAvailability,
  searchCommuterSchedules,
  ReservationDetails,
  ScheduleProduct,
  Schedules,
  searchSchedules,
  TDSCommuterSearchRequestBody,
  TDSScheduleCityToCityRequestBody,
  TDSScheduleCommuterRequestBody
} from '@/apis/tds';
import { checkBooking } from '@/apis/trailways';
import { LoginProvider } from '@/context/login-context';
import config from '@/utils/config';
import { formatDateString, formatDateYearMonthDay, getCarriers, getRescheduleOriginalFare } from '@/utils/schedules';
import logger from '@/logger';

type ScheduleResults = {
  destinationCityData: StrapiCityDatum;
  originCityData: StrapiCityDatum;
  outboundSchedules: ScheduleProduct[];
  returnSchedules?: ScheduleProduct[];
  outboundSchedulesAllPassengerType?: ScheduleProduct[];
  returnSchedulesAllPassengerType?: ScheduleProduct[];
  outboundReissueSchedules?: ScheduleProduct[];
  returnReissueSchedules?: ScheduleProduct[];
};

export type ItineraryItem = ScheduleProduct & {
  fare: Fare | null;
  isReturnTrip: boolean;
  type: string;
};

export type Itinerary = {
  outbound: ItineraryItem;
  returning?: ItineraryItem;
  rescheduleLeg?: string;
};

export type Charges = {
  addons: {
    addonId: number;
    disclaimer: string | null;
    disclaimerHeader: string | null;
    maxUse: number;
    name: string;
    numUses: number;
    perItemPrice: number;
    perPassenger: boolean;
    perRoute: boolean;
  }[];
  allowanceOptions: {
    maxCreditsAllowed: number;
    maxLoyaltyAllowed: number;
    usingCredits: boolean;
    usingLoyalty: boolean;
  }[];
  allowanceRules: {
    creditsAllowed: boolean;
    creditsRequired: boolean;
    loyaltyAllowed: boolean;
  };
  baggageFees: number;
  cartTimeRemaining: number;
  creditRules: {
    automaticUse: boolean;
    commuterAllowed: boolean;
    faresOnly: boolean;
    minPurchaseValue: number;
    rescheduleAllowed: boolean;
  };
  credits: number;
  discountTotal: number;
  feeTotal: number;
  fees: {
    [key: string]: number;
  };
  loyaltyRules: {
    commuterAllowed: boolean;
    faresOnly: boolean;
    minValueForPurchase: number;
    rescheduleAllowed: boolean;
  };
  manualCredits: number;
  manualLoyalty: number;
  maxCheckedBags: number;
  originalAmount: number;
  outboundFares: {
    Adult: {
      amount: number;
      amountBeforePromoCode?: number;
      cancelable: boolean;
      fareId: number;
      groupRate: boolean;
      name: string;
      originalAmount: number;
      passengerType: string;
      rescheduleAllPassengers: boolean;
      rescheduleable: boolean;
      roundTrip: boolean;
      type: string;
    }[];
  };
  promoCodeDiscount?: number;
  promoCodeError?: string;
  passengerType: string;
  purchaseCredits: number;
  purchaseLoyalty: number;
  referralLoyalty: number;
  remainingCredits: number;
  rescheduleAllPassengers: boolean;
  rescheduleable: boolean;
  returnFares?: {
    Adult: {
      amount: number;
      amountBeforePromoCode?: number;
      cancelable: boolean;
      fareId: number;
      groupRate: boolean;
      name: string;
      originalAmount: number;
      passengerType: string;
      rescheduleAllPassengers: boolean;
      rescheduleable: boolean;
      roundTrip: boolean;
      type: string;
    }[];
  };
  roundTrip: boolean;
  stacked: boolean;
  subtotal: number;
  taxTotal: number;
  total: number;
  totalBeforeDeductions: number;
  totalCustomerCredits: number;
  totalLoyaltyDollars: number;
  totalLoyaltyPoints: number;
  totalLoyaltyUsed: number;
  usingCredits: boolean;
};

export type TripDetailsForm = {
  ada: boolean;
  adults: number;
  children: number;
  departDate: string;
  destination: StrapiCityDatum['attributes'];
  isRoundTrip: boolean;
  modifyTrip?: boolean;
  origin: StrapiCityDatum['attributes'];
  passengers: {
    adults: Passenger[];
    children: Passenger[];
    seniors: Passenger[];
  };
  petCount?: number;
  returnDate: string;
  seniors: number;
  shouldRefresh: boolean;
  webTransactionId: string;
  wheelchairs: number;
};

export type Passenger = {
  firstName: string;
  lastName: string;
  passengerId?: number;
  pet?: boolean;
  type: string;
  wheel: boolean;
};

type Props = {
  passengers: Passenger[];
};

export type TicketCartGTM = {
  affinity_id: string;
  affinity_id_sha256: string;
  arrive_city: {
    cityId: number;
    name: string;
  };
  arrive_state: {
    abbreviation: string;
    country: string;
    name: string;
  };
  arrive_zip_code: string;
  bags: number;
  cart_date: string;
  cart_time: string;
  currency: string;
  delivery_email: string;
  delivery_email_sha256: string;
  depart_city: {
    cityId: number;
    name: string;
  };
  depart_date?: string;
  depart_state: {
    abbreviation: string;
    country: string;
    name: string;
  };
  depart_zip_code: string;
  number_pax: number;
  outbound_arrive_date?: string;
  outbound_arrive_datetime_utc?: string;
  outbound_arrive_time?: string;
  outbound_carrier?: string;
  outbound_depart_datetime_utc?: string;
  outbound_depart_time?: string;
  outbound_vanity_fare?: string;
  promo_code: string | null;
  return_arrive_date?: string;
  return_arrive_datetime_utc?: string;
  return_arrive_time?: string;
  return_carrier?: string;
  return_date?: string;
  return_depart_datetime_utc?: string;
  return_depart_time?: string;
  returning_vanity_fare?: string;
  ticket_detail: string;
  ticket_type: string;
  web_transaction_id: string;
};

export type PaymentInfo = {
  status: string;
  details: {
    address1: string;
    address2: string;
    cardType: string;
    city: string;
    country: string;
    created?: number;
    expirationDate?: string;
    expirationMonth: string;
    expirationYear: string;
    firstSix: string;
    lastFour: string;
    firstName?: string;
    lastName?: string;
    nameOnCard: string;
    postalCode: string;
    state: string;
    storedPaymentId?: number;
    token?: string;
  };
};

export type BookingInfo = {
  previouslySubmitted?: boolean;
  confirmationCode: string;
  customerEmailSent: boolean;
  description: string;
  requestPayload: { [key: string]: any };
};

type City = {
  cityId: number;
  name: string;
};

type State = {
  name: string;
  abbreviation: string;
  country: string;
};

type StopHours = {
  [key: string]: {
    openDate: string;
    closeDate: string;
  };
};

type Stop = {
  stopId: number;
  carrierCode: string;
  carrierName: string;
  stationName: string;
  stopLocationId: number;
  latitude: number;
  longitude: number;
  businessName: string;
  stopDescription: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  mmcLocationCode: string;
  directionsForArrival: string;
  directionsForDeparture: string;
  directionsForIntraLineTransfer: string;
  directionsForInterLineTransfer: string;
  notes: string;
  parkingNotes: string;
  extraInfoLabel: string;
  extraInfoNote: string;
  description: string;
  hours: StopHours;
};

export type Station = {
  stopUuid: string;
  stationName: string;
  stationCode: string;
  address: string;
  zip: string;
  iataCode: string;
  city: City;
  state: State;
  timeZone: string;
  latitude: number;
  longitude: number;
  stops: Stop[];
};

type FareDetails = {
  scheduleSpecialFareId: number;
  fareName: string;
  fare: number;
  carrierId: number;
  restrictionText: string;
  ticketAbbrev: string;
  totalOneWayTrips: number;
  isUnlimited: boolean;
  expirationDays: number;
  travelEffectiveDate: string;
  travelExpirationDate: string;
  isExpiredAtEndOfEffectiveMonth: boolean;
  isSubjectToRescheduleFee: boolean;
  minutesNoticeBeforeRescheduleFee: number;
  minutesRescheduleAllowedAfterDeparture: number;
  isExtendable: boolean;
  rescheduleable: boolean;
  daysCanBeExtendedPastNow: number;
};

export type CommuterFareDetail = {
  fareDetails: FareDetails;
  originStation: Station;
  destinationStation: Station;
};

export type CommuterSearchForm = { [key: string]: any };

export type CommuterFareDetails = CommuterFareDetail[];

export type LoaderProps = {
  showLoader: boolean;
  isInitializedFrom: string;
};

export type CommuterPassPassenger = {
  firstName: string;
  lastName: string;
  wheelchair: boolean;
  type: string;
  outboundFare: {
    specialFareId: number;
    type: string;
  };
  outboundBags: {
    count: number;
  };
  wheel?: boolean;
};

export type CommuterPassSelectForm = {
  outbound: {
    origin: {
      stopUuid: string;
    };
    destination: {
      stopUuid: string;
    };
    occurrence: string;
    departDate: string;
  };
  passengers: {
    [key: string]: CommuterPassPassenger[];
  };
  useCredits: boolean;
  useLoyalty: boolean;
  addonRequests: any[];
  carrierId: number;
};

export type CommuterPassDetails = { [key: string]: any };

export type CommuterRideBooking = {
  bookStatus: string;
  confirmationCode?: string;
  confirmationNumber?: number;
  date: string;
  email?: string;
  manifestId?: string;
};

interface PerformTripSearchOptions {
  isOneWay: boolean;
  wheelchairCount: number;
  modifyTrip?: boolean;
  isDateChangedViaDateSelector?: boolean;
}

type AsyncFunction<T extends any[], R> = (...args: T) => Promise<R>;

interface TripSearchContextProps {
  getAvailability: (itinerary?: Itinerary, isCartRefresh?: boolean) => Promise<Charges | null>;
  getCommuterPassAvailability: (data?: CommuterPassSelectForm) => Promise<Charges | null>;
  getTripDetails: () => any;
  bookTicketRequest: (data: { [key: string]: any }) => Promise<BookingInfo | null>;
  resetTrip: () => void;
  resetTripPartial: () => void;
  performTripSearch: (search: TDSScheduleCityToCityRequestBody, options: PerformTripSearchOptions) => Promise<boolean>;
  withLoader: <T extends any[], R>(
    asyncFunction: (...args: T) => Promise<R | null>
  ) => (...args: T) => Promise<R | null>;
  scheduleResults: ScheduleResults | null;
  setScheduleResults: React.Dispatch<React.SetStateAction<ScheduleResults | null>>;
  setTripDetailsForm: React.Dispatch<React.SetStateAction<TripDetailsForm | null>>;
  tripDetailsForm: TripDetailsForm | null;
  itinerary: Itinerary | null;
  setItinerary: React.Dispatch<React.SetStateAction<Itinerary | null>>;
  charges: Charges | null;
  setCharges: React.Dispatch<React.SetStateAction<Charges | null>>;
  commuterPassCharges: Charges | null;
  setCommuterPassCharges: React.Dispatch<React.SetStateAction<Charges | null>>;
  ticketCartGTM: TicketCartGTM | null;
  setTicketCartGTM: React.Dispatch<React.SetStateAction<TicketCartGTM | null>>;
  paymentInfo: PaymentInfo | null;
  setPaymentInfo: React.Dispatch<React.SetStateAction<PaymentInfo | null>>;
  protectionSelected: boolean | null;
  setProtectionSelected: React.Dispatch<React.SetStateAction<boolean | null>>;
  kountSessionId: string | null;
  setKountSessionId: React.Dispatch<React.SetStateAction<string | null>>;
  bookingInfo: BookingInfo | null;
  setBookingInfo: React.Dispatch<React.SetStateAction<BookingInfo | null>>;
  isUsingLoyalty: boolean;
  setIsUsingLoyalty: React.Dispatch<React.SetStateAction<boolean>>;
  isUsingCredits: boolean;
  setIsUsingCredits: React.Dispatch<React.SetStateAction<boolean>>;
  promoCode: string | null;
  setPromoCode: React.Dispatch<React.SetStateAction<string | null>>;
  loyaltyAmountForBooking: number | null;
  setLoyaltyAmountForBooking: React.Dispatch<React.SetStateAction<number | null>>;
  redirectMessage: boolean;
  setRedirectMessage: React.Dispatch<React.SetStateAction<boolean>>;
  loaderProps: LoaderProps;
  setLoaderProps: React.Dispatch<React.SetStateAction<LoaderProps>>;
  cartTimerExpired: boolean;
  setCartTimerExpired: React.Dispatch<React.SetStateAction<boolean>>;
  isTripSelected: boolean;
  setIsTripSelected: React.Dispatch<React.SetStateAction<boolean>>;
  resetCommuterSearch: () => void;
  resetCommuterFareDetails: () => void;
  performCommuterSearch: (a: TDSCommuterSearchRequestBody) => Promise<boolean>;
  commuterFareDetails: CommuterFareDetails | null;
  setCommuterFareDetails: React.Dispatch<React.SetStateAction<CommuterFareDetails | null>>;
  setCommuterPassSelectForm: React.Dispatch<React.SetStateAction<CommuterPassSelectForm | null>>;
  commuterPassSelectForm: CommuterPassSelectForm | null;
  setCommuterPassDetails: React.Dispatch<React.SetStateAction<CommuterPassDetails | null>>;
  commuterPassDetails: CommuterPassDetails | null;
  bookCommuterPassTicketRequest: (data: { [key: string]: any }) => Promise<any | null>;
  commuterPass: CommuterPass | null;
  setCommuterPass: React.Dispatch<React.SetStateAction<CommuterPass | null>>;
  commuterSchedules: Schedules | null;
  setCommuterSchedules: React.Dispatch<React.SetStateAction<Schedules | null>>;
  performCommuterSchedulesSearch: (a: TDSScheduleCommuterRequestBody) => Promise<boolean>;
  commuterRideBookings: CommuterRideBooking[];
  setCommuterRideBookings: React.Dispatch<React.SetStateAction<CommuterRideBooking[]>>;
  reservationDetails: ReservationDetails | null;
  setReservationDetails: React.Dispatch<React.SetStateAction<ReservationDetails | null>>;
  isReturnDateChanged: boolean;
  setIsReturnDateChanged: React.Dispatch<React.SetStateAction<boolean>>;
  closeAllModals: boolean;
  setCloseAllModals: React.Dispatch<React.SetStateAction<boolean>>;
  shouldHideSchedules: boolean;
  setShouldHideSchedules: React.Dispatch<React.SetStateAction<boolean>>;
}

const TripSearchContext = createContext<TripSearchContextProps | undefined>(undefined);

interface SchedulesProviderProps {
  children: ReactNode;
}

const useInitialState = () => {
  const [scheduleResults, setScheduleResults] = useState<ScheduleResults | null>(null);
  const [tripDetailsForm, setTripDetailsForm] = useState<TripDetailsForm | null>(null);
  const [itinerary, setItinerary] = useState<Itinerary | null>(null);
  const [charges, setCharges] = useState<Charges | null>(null);
  const [commuterPassCharges, setCommuterPassCharges] = useState<Charges | null>(null);
  const [ticketCartGTM, setTicketCartGTM] = useState<TicketCartGTM | null>(null);
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [protectionSelected, setProtectionSelected] = useState<boolean | null>(null);
  const [kountSessionId, setKountSessionId] = useState<string | null>(null);
  const [bookingInfo, setBookingInfo] = useState<BookingInfo | null>(null);
  const [loyaltyAmountForBooking, setLoyaltyAmountForBooking] = useState<number | null>(null);
  const [isUsingLoyalty, setIsUsingLoyalty] = useState(false);
  const [isUsingCredits, setIsUsingCredits] = useState(false);
  const [promoCode, setPromoCode] = useState<string | null>(null);
  const [redirectMessage, setRedirectMessage] = useState(false);
  const [loaderProps, setLoaderProps] = useState({ showLoader: false, isInitializedFrom: '' });
  const [cartTimerExpired, setCartTimerExpired] = useState(false);
  const [isTripSelected, setIsTripSelected] = useState<boolean>(false);
  const [commuterFareDetails, setCommuterFareDetails] = useState<CommuterFareDetails | null>(null);
  const [commuterPassSelectForm, setCommuterPassSelectForm] = useState<CommuterPassSelectForm | null>(null);
  const [commuterPassDetails, setCommuterPassDetails] = useState<CommuterPassDetails | null>(null);
  const [commuterPass, setCommuterPass] = useState<CommuterPass | null>(null);
  const [commuterSchedules, setCommuterSchedules] = useState<Schedules | null>(null);
  const [commuterRideBookings, setCommuterRideBookings] = useState<CommuterRideBooking[]>([]);
  const [reservationDetails, setReservationDetails] = useState<ReservationDetails | null>(null);
  const [isReturnDateChanged, setIsReturnDateChanged] = useState<boolean>(false);
  const [closeAllModals, setCloseAllModals] = useState<boolean>(false);
  const [shouldHideSchedules, setShouldHideSchedules] = useState<boolean>(false);
  return {
    getAvailability: async (itinerary?: Itinerary, isCartRefresh?: boolean): Promise<Charges | null> => null,
    getCommuterPassAvailability: async (data?: CommuterPassSelectForm): Promise<Charges | null> => null,
    getTripDetails: () => null,
    bookTicketRequest: async (data: { [key: string]: any }): Promise<BookingInfo | null> => null,
    bookCommuterPassTicketRequest: async (data: { [key: string]: any }): Promise<any | null> => null,
    resetTrip: () => null,
    resetTripPartial: () => null,
    performTripSearch: async (search: TDSScheduleCityToCityRequestBody, options: PerformTripSearchOptions) => false,
    withLoader:
      <T extends any[], R>(asyncFunction: (...args: T) => Promise<R | null>) =>
      (...args: T) =>
        Promise.resolve(null as R),
    resetCommuterSearch: () => null,
    resetCommuterFareDetails: () => null,
    performCommuterSearch: async (a: TDSCommuterSearchRequestBody) => false,
    performCommuterSchedulesSearch: async (a: TDSScheduleCommuterRequestBody) => false,
    charges,
    setCharges,
    commuterPassCharges,
    setCommuterPassCharges,
    itinerary,
    setItinerary,
    scheduleResults,
    setScheduleResults,
    setTripDetailsForm,
    tripDetailsForm,
    ticketCartGTM,
    setTicketCartGTM,
    paymentInfo,
    setPaymentInfo,
    protectionSelected,
    setProtectionSelected,
    kountSessionId,
    setKountSessionId,
    bookingInfo,
    setBookingInfo,
    isUsingLoyalty,
    setIsUsingLoyalty,
    isUsingCredits,
    setIsUsingCredits,
    promoCode,
    setPromoCode,
    loyaltyAmountForBooking,
    setLoyaltyAmountForBooking,
    redirectMessage,
    setRedirectMessage,
    loaderProps,
    setLoaderProps,
    cartTimerExpired,
    setCartTimerExpired,
    isTripSelected,
    setIsTripSelected,
    commuterFareDetails,
    setCommuterFareDetails,
    commuterPassSelectForm,
    setCommuterPassSelectForm,
    commuterPassDetails,
    setCommuterPassDetails,
    commuterPass,
    setCommuterPass,
    commuterSchedules,
    setCommuterSchedules,
    commuterRideBookings,
    setCommuterRideBookings,
    reservationDetails,
    setReservationDetails,
    isReturnDateChanged,
    setIsReturnDateChanged,
    closeAllModals,
    setCloseAllModals,
    shouldHideSchedules,
    setShouldHideSchedules
  };
};

export const TripSearchProvider: React.FC<SchedulesProviderProps> = ({ children }) => {
  const [scheduleResults, setScheduleResults] = useState<ScheduleResults | null>(null);
  const [tripDetailsForm, setTripDetailsForm] = useState<TripDetailsForm | null>(null);
  const [itinerary, setItinerary] = useState<Itinerary | null>(null);
  const [charges, setCharges] = useState<Charges | null>(null);
  const [commuterPassCharges, setCommuterPassCharges] = useState<Charges | null>(null);
  const [ticketCartGTM, setTicketCartGTM] = useState<TicketCartGTM | null>(null);
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [protectionSelected, setProtectionSelected] = useState<boolean | null>(null);
  const [isUsingLoyalty, setIsUsingLoyalty] = useState(false);
  const [isUsingCredits, setIsUsingCredits] = useState(false);
  const [promoCode, setPromoCode] = useState<string | null>(null);
  const [kountSessionId, setKountSessionId] = useState<string | null>(null);
  const [bookingInfo, setBookingInfo] = useState<BookingInfo | null>(null);
  const [loyaltyAmountForBooking, setLoyaltyAmountForBooking] = useState<number | null>(null);
  const [redirectMessage, setRedirectMessage] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [loaderProps, setLoaderProps] = useState({ showLoader: false, isInitializedFrom: '' });
  const [cartTimerExpired, setCartTimerExpired] = useState(false);
  const [isTripSelected, setIsTripSelected] = useState<boolean>(false);
  const [commuterFareDetails, setCommuterFareDetails] = useState<CommuterFareDetails | null>(null);
  const [commuterPassSelectForm, setCommuterPassSelectForm] = useState<CommuterPassSelectForm | null>(null);
  const [commuterPassDetails, setCommuterPassDetails] = useState<CommuterPassDetails | null>(null);
  const [commuterPass, setCommuterPass] = useState<CommuterPass | null>(null);
  const [commuterSchedules, setCommuterSchedules] = useState<Schedules | null>(null);
  const [commuterRideBookings, setCommuterRideBookings] = useState<CommuterRideBooking[]>([]);
  const [reservationDetails, setReservationDetails] = useState<ReservationDetails | null>(null);
  const [isReturnDateChanged, setIsReturnDateChanged] = useState<boolean>(false);
  const [closeAllModals, setCloseAllModals] = useState<boolean>(false);
  const [shouldHideSchedules, setShouldHideSchedules] = useState<boolean>(false);

  const setState = (key: string, setter: React.Dispatch<React.SetStateAction<any>>) => {
    const dataFromStorage = sessionStorage.getItem(key);
    if (dataFromStorage) {
      setter(JSON.parse(dataFromStorage));
    }
  };

  useEffect(() => {
    setState('scheduleResults', setScheduleResults);
    setState('tripDetailsForm', setTripDetailsForm);
    setState('itinerary', setItinerary);
    setState('charges', setCharges);
    setState('commuterPassCharges', setCommuterPassCharges);
    setState('ticketCartGTM', setTicketCartGTM);
    setState('paymentInfo', setPaymentInfo);
    setState('protectionSelected', setProtectionSelected);
    setState('kountSessionId', setKountSessionId);
    setState('bookingInfo', setBookingInfo);
    setState('loyaltyAmountForBooking', setLoyaltyAmountForBooking);
    setState('isUsingLoyalty', setIsUsingLoyalty);
    setState('isUsingCredits', setIsUsingCredits);
    setState('promoCode', setPromoCode);
    setState('redirectMessage', setRedirectMessage);
    setState('loaderProps', setLoaderProps);
    setState('cartTimerExpired', setCartTimerExpired);
    setState('isTripSelected', setIsTripSelected);
    setState('commuterFareDetails', setCommuterFareDetails);
    setState('commuterPassSelectForm', setCommuterPassSelectForm);
    setState('commuterPassDetails', setCommuterPassDetails);
    setState('commuterPass', setCommuterPass);
    setState('commuterSchedules', setCommuterSchedules);
    setState('commuterRideBookings', setCommuterRideBookings);
    setState('reservationDetails', setReservationDetails);
    setState('isReturnDateChanged', setIsReturnDateChanged);
    setState('closeAllModals', setCloseAllModals);
    setState('shouldHideSchedules', setShouldHideSchedules);
    setIsReady(true);
  }, []);

  const setData = (key: string, data: any) => {
    if (isReady) {
      sessionStorage.setItem(key, JSON.stringify(data));
    }
  };

  useEffect(() => {
    setData('scheduleResults', scheduleResults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleResults]);

  useEffect(() => {
    setData('tripDetailsForm', tripDetailsForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripDetailsForm]);

  useEffect(() => {
    setData('itinerary', itinerary);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itinerary]);

  useEffect(() => {
    setData('charges', charges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charges]);

  useEffect(() => {
    setData('commuterPassCharges', commuterPassCharges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commuterPassCharges]);

  useEffect(() => {
    setData('ticketCartGTM', ticketCartGTM);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketCartGTM]);

  useEffect(() => {
    setData('paymentInfo', paymentInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentInfo]);

  useEffect(() => {
    setData('protectionSelected', protectionSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protectionSelected]);

  useEffect(() => {
    setData('kountSessionId', kountSessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kountSessionId]);

  useEffect(() => {
    setData('bookingInfo', bookingInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingInfo]);

  useEffect(() => {
    setData('loyaltyAmountForBooking', loyaltyAmountForBooking);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loyaltyAmountForBooking]);

  useEffect(() => {
    setData('isUsingLoyalty', isUsingLoyalty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUsingLoyalty]);

  useEffect(() => {
    setData('isUsingCredits', isUsingCredits);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUsingCredits]);

  useEffect(() => {
    setData('promoCode', promoCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoCode]);

  useEffect(() => {
    setData('redirectMessage', redirectMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectMessage]);

  useEffect(() => {
    setData('loaderProps', loaderProps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaderProps]);

  useEffect(() => {
    setData('cartTimerExpired', cartTimerExpired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartTimerExpired]);

  useEffect(() => {
    setData('isTripSelected', isTripSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTripSelected]);

  useEffect(() => {
    setData('commuterFareDetails', commuterFareDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commuterFareDetails]);

  useEffect(() => {
    setData('commuterPassSelectForm', commuterPassSelectForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commuterPassSelectForm]);

  useEffect(() => {
    setData('commuterPassDetails', commuterPassDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commuterPassDetails]);

  useEffect(() => {
    setData('commuterPass', commuterPass);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commuterPass]);

  useEffect(() => {
    setData('commuterSchedules', commuterSchedules);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commuterSchedules]);

  useEffect(() => {
    setData('commuterRideBookings', commuterRideBookings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commuterRideBookings]);

  const performCommuterSearch = async (search: TDSCommuterSearchRequestBody) => {
    const commuterFareDetails = await commuterSearch(search);
    if (commuterFareDetails && commuterFareDetails.length) {
      setCommuterFareDetails(commuterFareDetails);
      return true;
    }
    return false;
  };

  const performCommuterSchedulesSearch = async (search: TDSScheduleCommuterRequestBody) => {
    const commuterSchedules = await searchCommuterSchedules(search);
    if (commuterSchedules?.scheduleProducts?.length) {
      setCommuterSchedules(commuterSchedules);
      setCommuterPass((prev) => (prev ? { ...prev, schedulesFor: [search.startDate] } : null));
      return true;
    }
    return false;
  };

  const resetCommuterSearch = () => {
    setCommuterRideBookings([]);
    setCommuterPassSelectForm(null);
    setCommuterPassDetails(null);
    resetCommuterFareDetails();
  };

  const resetCommuterFareDetails = () => {
    setCommuterFareDetails(null);
    setCommuterPassCharges(null);
    setPaymentInfo(null);
    setKountSessionId(null);
    setBookingInfo(null);
    setIsUsingCredits(false);
    setIsUsingLoyalty(false);
  };
  useEffect(() => {
    setData('reservationDetails', reservationDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationDetails]);

  useEffect(() => {
    setData('isReturnDateChanged', isReturnDateChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReturnDateChanged]);

  useEffect(() => {
    setData('closeAllModals', closeAllModals);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeAllModals]);

  useEffect(() => {
    setData('shouldHideSchedules', shouldHideSchedules);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldHideSchedules]);

  const getAvailability = async (
    updatedItinerary?: Itinerary | null,
    isCartRefresh?: boolean,
    attempts = 0
  ): Promise<Charges | null> => {
    updatedItinerary = updatedItinerary ?? itinerary;
    if (updatedItinerary && tripDetailsForm) {
      const maxRetries = 1;

      const response = await fetchAvailability(
        scheduleResults,
        updatedItinerary,
        tripDetailsForm,
        charges,
        protectionSelected,
        !(isCartRefresh && promoCode) ? isUsingLoyalty : false,
        !(isCartRefresh && promoCode) ? isUsingCredits : false,
        !isCartRefresh ? promoCode : null,
        reservationDetails
      );
      if (response.statusCode && response.statusCode !== 200) {
        if (response.message === 'Fares for requested passengers not available.' && attempts < maxRetries) {
          // Retry recursively

          return await withLoader(getAvailability)(updatedItinerary, isCartRefresh, attempts + 1);
        }
        // If max retries reached or a different error, throw the response error
        throw response;
      }

      // Process response data after a successful request
      const data = await response.json();

      /*
       * The following code ensures the Payment Summary section displays the correct passenger fares during rebooking
       * with a promo code.
       * During rebooking, the 'amount' property from the availability output is used to display the fare instead of
       * 'originalAmount, as 'originalAmount' denotes the fare of the original trip.
       * When a promo code is applied, the 'amount' is adjusted for the discount, which can lead to an incorrect fare
       * being displayed in the Payment Summary.
       * To address this, the pre-discount 'amount' is retrieved from the previous state (charges), stored in
       * 'amountBeforePromoCode', and used when displaying the fare in the Payment Summary section.
       */
      if (updatedItinerary.rescheduleLeg && data.promoCodeDiscount) {
        const addAmountBeforePromoCode = (passengerType: string) => {
          if (data.outboundFares[passengerType]) {
            data.outboundFares[passengerType].forEach((item: Charges['outboundFares']['Adult'][0], index: number) => {
              data.outboundFares[passengerType][index].amountBeforePromoCode =
                charges?.outboundFares?.[passengerType as keyof typeof charges.outboundFares]?.[index]
                  ?.amountBeforePromoCode ||
                charges?.outboundFares?.[passengerType as keyof typeof charges.outboundFares]?.[index]?.amount ||
                item.amount;
            });
          }
          if (data.returnFares?.[passengerType]) {
            data.returnFares[passengerType].forEach((item: Charges['outboundFares']['Adult'][0], index: number) => {
              data.returnFares[passengerType][index].amountBeforePromoCode =
                charges?.returnFares?.[passengerType as keyof typeof charges.returnFares]?.[index]
                  ?.amountBeforePromoCode ||
                charges?.returnFares?.[passengerType as keyof typeof charges.returnFares]?.[index]?.amount ||
                item.amount;
            });
          }
        };

        ['Adult', 'Child', 'Senior'].forEach(addAmountBeforePromoCode);
      }

      setCharges(data);
      if (data.loyaltyRedemption) {
        setLoyaltyAmountForBooking(data.loyaltyRedemption);
      }
      if (data.credits !== 0 && data.creditRules.automaticUse && !updatedItinerary.rescheduleLeg) {
        setIsUsingCredits(true);
      }
      if (!getProtectionAddon(data?.addons) && protectionSelected) {
        setProtectionSelected(null);
      }
      return data;
    }
    return null;
  };

  const getCommuterPassAvailability = async (data?: CommuterPassSelectForm, attempts = 0) => {
    const maxRetries = 1;
    const payload = data ?? commuterPassSelectForm ?? ({} as CommuterPassSelectForm);
    payload.useLoyalty = isUsingLoyalty;
    payload.useCredits = isUsingCredits;
    const response = await fetchCommuterPassAvailability(payload);
    if (response.statusCode && response.statusCode !== 200) {
      if (response.message === 'Fares for requested passengers not available.' && attempts < maxRetries) {
        // Retry recursively
        return getCommuterPassAvailability(data, attempts + 1);
      }
      // If max retries reached or a different error, throw the response error
      throw response;
    }

    // Process response data after a successful request
    const jsonResponse = await response.json();
    setCommuterPassCharges(jsonResponse);
    if (jsonResponse.credits !== 0 && jsonResponse.creditRules.automaticUse) {
      setIsUsingCredits(true);
    }
    return jsonResponse;
  };

  const getTripDetails = () => {
    if (scheduleResults && itinerary && tripDetailsForm) {
      return buildTripDetails(scheduleResults, itinerary, tripDetailsForm);
    }
    return null;
  };

  const bookTicketRequest = async (payload: { [key: string]: any }) => {
    if (itinerary) {
      const isReissue = !!(
        reservationDetails &&
        (scheduleResults?.outboundReissueSchedules || scheduleResults?.returnReissueSchedules) &&
        itinerary.rescheduleLeg
      );

      payload = buildBookTicketPayload(payload, itinerary, scheduleResults, isReissue);

      if (isReissue) {
        payload.purchaseType = 'REISSUE_BOOK';
        payload.reissueRequest = buildReissueRequestPayload(
          itinerary,
          reservationDetails,
          tripDetailsForm,
          payload.purchaseType
        );
        if (charges && charges.promoCodeDiscount) {
          const sellerOverride = charges.subtotal / payload.reissueRequest.passengers.length;
          payload.reissueRequest.passengers.forEach((passenger: any) => {
            passenger.outboundFare.sellerOverride = Number(sellerOverride.toFixed(2));
          });
        }
        delete payload.useCredits;
        if (
          itinerary.rescheduleLeg === 'return' &&
          reservationDetails.directions.length === 1 &&
          reservationDetails.directions[0] === 'RETURNING'
        ) {
          payload.outbound = payload.returning;
          delete payload.returning;
        }
      }

      const cartTimeRemaining = charges?.cartTimeRemaining;
      if (promoCode) {
        payload.promoCode = promoCode;
      }
      payload.cartTimeRemaining = cartTimeRemaining;

      const { getReusableKeyFromBookingInfo } = await import('@/utils/book');
      const key = getReusableKeyFromBookingInfo(payload);
      let response = null;
      try {
        response = await bookTicket(payload);
      } catch (error) {
        logger.log('error', 'Error booking ticket ' + error);
      }
      // Handle failures.  First try to see if it's a duplicate, or if the booking call timed out
      if (!response || (response && response.statusCode && response.statusCode !== 200)) {
        // If server explicitly says booking was a duplicate, that means the user tried to submit twice somehow.  Show an error.
        // We don't show the confirmation change, in case the user changed something in between attempts
        if (response && response.statusCode === 500 && response.error === 'Booking already exists') {
          logger.log(
            'error',
            'Booking already exists for ' + payload.buyer.email + ' with key ' + key + '.  Not redirecting'
          );
          return { previouslySubmitted: true };
        }
        // If server didn't say it was a duplicate, but we find a completed purchase, redirect to confirmation page
        // since that means there was probably a network error and the purchase went through
        const checkBookingResponse = await checkBooking(key);
        if (Object.keys(checkBookingResponse).length > 0) {
          logger.log(
            'error',
            'Booking already exists for ' +
              payload.buyer.email +
              ' with key ' +
              key +
              '.  Redirect to confirmation page'
          );
          response = checkBookingResponse; // Use the response from Redis
        }
      }
      // If it definitely failed, throw the error
      if (!response || (response && response.statusCode && response.statusCode !== 200)) {
        throw response;
      }

      // Handle successes
      setBookingInfo({
        ...response,
        requestPayload: payload
      });
      return response;
    }
    return null;
  };

  const bookCommuterPassTicketRequest = async (payload: { [key: string]: any }) => {
    const { getReusableKeyFromBookingInfo } = await import('@/utils/book');
    const key = getReusableKeyFromBookingInfo(payload);
    let response = await commuterPassBookTicket(payload);

    // Handle failures
    if (response === null || (response.statusCode && response.statusCode !== 200)) {
      // If server explictly says booking was a duplicate, that means the user tried to submit twice somehow.  Show an error.
      // We don't show the confirmation change, in case the user changed something in between attempts
      if (response && response.statusCode === 500 && response.error === 'Booking already exists') {
        logger.log(
          'error',
          'Booking already exists for ' + payload.buyer.email + ' with key ' + key + '.  Not redirecting'
        );
        return { previouslySubmitted: true };
      } else {
        // If server didn't say it was a duplicate, but we find a completed purchase, redirect to confirmation page
        // since that means there was probably a network error and the purchase went through
        const checkBookingResponse = await checkBooking(key);
        if (Object.keys(checkBookingResponse).length > 0) {
          logger.log(
            'error',
            'Booking already exists for ' +
              payload.buyer.email +
              ' with key ' +
              key +
              '.  Redirect to confirmation page'
          );
          response = checkBookingResponse; // Use the response from Redis
        } else if (response.statusCode && response.statusCode !== 200) {
          throw response;
        }
      }
    }
    setBookingInfo({
      ...response,
      requestPayload: payload
    });
    return response;
  };

  const withLoader = <T extends any[], R>(
    asyncFunction: (...args: T) => Promise<R | null>
  ): ((...args: T) => Promise<R | null>) => {
    return async (...args: T): Promise<R> => {
      try {
        setLoaderProps({ showLoader: true, isInitializedFrom: '' });
        return (await asyncFunction(...args)) as R;
      } finally {
        setTimeout(() => {
          setLoaderProps({ showLoader: false, isInitializedFrom: '' });
        }, 300);
      }
    };
  };

  const performTripSearch = async (search: TDSScheduleCityToCityRequestBody, options: PerformTripSearchOptions) => {
    interface Passenger {
      firstName: string;
      lastName: string;
      type: string;
      wheel: boolean;
    }
    const passengers: TripDetailsForm['passengers'] = {
      adults: [],
      children: [],
      seniors: []
    };
    const passengersObject: { [key: string]: { passengerType: string; count: number | undefined } } = {
      adults: {
        passengerType: 'adults',
        count: search?.passengerCounts?.Adult
      },
      children: {
        passengerType: 'children',
        count: search?.passengerCounts?.Child
      },
      seniors: {
        passengerType: 'seniors',
        count: search?.passengerCounts?.Senior
      }
    };

    for (const key in passengersObject) {
      if (Object.prototype.hasOwnProperty.call(passengersObject, key)) {
        const passenger = passengersObject[key];
        if (passenger.count !== undefined) {
          for (let counter = 0; counter < passenger.count; counter++) {
            const newPassenger: Passenger = {
              firstName: '',
              lastName: '',
              type: passenger.passengerType,
              wheel: false
            };
            passengers[passenger.passengerType as keyof typeof passengers].push(newPassenger);
          }
        }
      }
    }

    const { results, resultsAllPassengerType } = await searchSchedules(search);
    if (options.isDateChangedViaDateSelector) {
      const data: TripDetailsForm = {
        ada: options.wheelchairCount > 0,
        adults: search?.passengerCounts?.Adult || 0,
        children: search?.passengerCounts?.Child || 0,
        departDate: search.departDate,
        destination: results.destinationCityData.attributes,
        isRoundTrip: !options.isOneWay,
        origin: results.originCityData.attributes,
        passengers: passengers,
        returnDate: search.returnDate || '',
        seniors: search?.passengerCounts?.Senior || 0,
        shouldRefresh: false,
        webTransactionId: uuidv4(),
        wheelchairs: options.wheelchairCount,
        modifyTrip: options.modifyTrip
      };
      setTripDetailsForm(data);
    }
    setShouldHideSchedules(false);
    if (results && results.schedules.length) {
      setScheduleResults({
        outboundSchedules: results.schedules,
        originCityData: results.originCityData,
        destinationCityData: results.destinationCityData,
        outboundSchedulesAllPassengerType: resultsAllPassengerType?.schedules
      });
      const data: TripDetailsForm = {
        ada: options.wheelchairCount > 0,
        adults: search?.passengerCounts?.Adult || 0,
        children: search?.passengerCounts?.Child || 0,
        departDate: search.departDate,
        destination: results.destinationCityData.attributes,
        isRoundTrip: !options.isOneWay,
        origin: results.originCityData.attributes,
        passengers: passengers,
        returnDate: search.returnDate || '',
        seniors: search?.passengerCounts?.Senior || 0,
        shouldRefresh: false,
        webTransactionId: uuidv4(),
        wheelchairs: options.wheelchairCount,
        modifyTrip: options.modifyTrip
      };
      setTripDetailsForm(data);
      return true;
    }
    setLoaderProps({ showLoader: false, isInitializedFrom: '' });
    if (options.isDateChangedViaDateSelector) {
      setShouldHideSchedules(true);
    }
    return false;
  };

  const resetTrip = () => {
    setScheduleResults(null);
    setTripDetailsForm(null);
    setItinerary(null);
    resetTripPartial();
  };

  const resetTripPartial = () => {
    setCharges(null);
    setTicketCartGTM(null);
    setPaymentInfo(null);
    setProtectionSelected(null);
    setKountSessionId(null);
    setBookingInfo(null);
    setIsUsingCredits(false);
    setIsUsingLoyalty(false);
    setPromoCode(null);
    setLoyaltyAmountForBooking(null);
    setReservationDetails(null);
  };

  return isReady ? (
    <TripSearchContext.Provider
      value={{
        bookTicketRequest,
        bookCommuterPassTicketRequest,
        getAvailability,
        getCommuterPassAvailability,
        getTripDetails,
        resetTrip,
        resetTripPartial,
        performTripSearch,
        withLoader,
        scheduleResults,
        setScheduleResults,
        tripDetailsForm,
        setTripDetailsForm,
        itinerary,
        setItinerary,
        charges,
        setCharges,
        commuterPassCharges,
        setCommuterPassCharges,
        ticketCartGTM,
        setTicketCartGTM,
        paymentInfo,
        setPaymentInfo,
        protectionSelected,
        setProtectionSelected,
        kountSessionId,
        setKountSessionId,
        bookingInfo,
        setBookingInfo,
        isUsingLoyalty,
        setIsUsingLoyalty,
        isUsingCredits,
        setIsUsingCredits,
        promoCode,
        setPromoCode,
        loyaltyAmountForBooking,
        setLoyaltyAmountForBooking,
        redirectMessage,
        setRedirectMessage,
        loaderProps,
        setLoaderProps,
        cartTimerExpired,
        setCartTimerExpired,
        isTripSelected,
        setIsTripSelected,
        resetCommuterSearch,
        resetCommuterFareDetails,
        performCommuterSearch,
        commuterFareDetails,
        setCommuterFareDetails,
        commuterPassSelectForm,
        setCommuterPassSelectForm,
        commuterPassDetails,
        setCommuterPassDetails,
        commuterPass,
        setCommuterPass,
        commuterSchedules,
        setCommuterSchedules,
        performCommuterSchedulesSearch,
        commuterRideBookings,
        setCommuterRideBookings,
        reservationDetails,
        setReservationDetails,
        isReturnDateChanged,
        setIsReturnDateChanged,
        closeAllModals,
        setCloseAllModals,
        shouldHideSchedules,
        setShouldHideSchedules
      }}
    >
      <LoginProvider>{children}</LoginProvider>
    </TripSearchContext.Provider>
  ) : null;
};

export const useTripSearch = (): TripSearchContextProps => {
  const initialState = useInitialState();
  const context = useContext(TripSearchContext);
  return context || initialState;
};

const buildBookTicketPayload = (
  payload: { [key: string]: any },
  itinerary: Itinerary,
  scheduleResults: ScheduleResults | null,
  isReissue: boolean
) => {
  const buildPassengers = (passengers: Passenger[], type: string) => {
    return passengers.map((passenger) => {
      const outboundFare: any = getFareByPassengerType(
        itinerary.outbound,
        scheduleResults?.outboundSchedulesAllPassengerType,
        type
      );
      const item: { [key: string]: any } = {
        firstName: passenger.firstName,
        lastName: passenger.lastName,
        wheelchair: passenger.wheel,
        type,
        outboundFare: {
          mileageFareId: outboundFare?.mileageFareId,
          specialFareId: outboundFare?.specialFareId,
          type: outboundFare?.type,
          markup: outboundFare?.markup
        },
        outboundBags: {
          count: 0
        }
      };
      if (itinerary.returning) {
        const returnFare: any = getFareByPassengerType(
          itinerary.returning,
          scheduleResults?.returnSchedulesAllPassengerType,
          type
        );
        item.returningFare = {
          mileageFareId: returnFare?.mileageFareId,
          specialFareId: returnFare?.specialFareId,
          type: returnFare?.type,
          markup: returnFare?.markup
        };
        item.returningBags = {
          count: 0
        };
      }
      return item;
    });
  };

  let passengerCounts = {};
  if (!isReissue) {
    const adults = buildPassengers(payload.passengers.adults, 'Adult');
    const seniors = buildPassengers(payload.passengers.seniors, 'Senior');
    const children = buildPassengers(payload.passengers.children, 'Child');
    if (adults && adults.length) {
      passengerCounts = {
        ...passengerCounts,
        Adult: adults
      };
    }
    if (seniors && seniors.length) {
      passengerCounts = {
        ...passengerCounts,
        Senior: seniors
      };
    }
    if (children && children.length) {
      passengerCounts = {
        ...passengerCounts,
        Child: children
      };
    }
  }

  payload = {
    addons: payload.addons.length ? payload.addons : undefined,
    buyer: {
      address1: payload.buyer.address1,
      address2: payload.buyer.address2,
      city: payload.buyer.city,
      email: payload.buyer.email,
      firstName: payload.buyer.firstName,
      lastName: payload.buyer.lastName,
      mobile: payload.buyer.mobile || payload.buyer.phone,
      phone: payload.buyer.phone || payload.buyer.mobile,
      state: payload.buyer.state,
      zip: payload.buyer.postalCode
    },
    outbound: {
      departDate: payload.departDate,
      destination: payload.destination,
      occurrence: 1,
      origin: payload.origin,
      scheduleUuid: payload.scheduleUuid
    },
    passengerCounts: passengerCounts,
    paymentsInfo: [
      {
        accountNumber: payload.payment.token,
        amount: Number(payload.payment.amount.toFixed(2)),
        cardType: payload.cardType,
        country: payload.buyer.country,
        createProfile: payload.payment.createProfile ? payload.payment.createProfile : false,
        expirationMonth: payload.buyer.expirationMonth,
        expirationYear: payload.buyer.expirationYear,
        firstSix: payload.buyer.firstSix,
        fraudMerchantId: config.kount.kountMerchId,
        fraudSessionId: payload.payment.fraudSessionId,
        lastFour: payload.buyer.lastFour,
        paymentMethod: 'ONLINE',
        storedPaymentId: payload.buyer.storedPaymentId,
        token: payload.payment.token,
        transactionDate: new Date().toISOString(),
        transactionType: 'CREDIT_CARD'
      }
    ],
    purchaseType: 'SCHEDULE_BOOK',
    returning: payload.returning,
    useCredits: payload.isUsingCredits,
    useLoyalty: payload.isUsingLoyalty
  };
  return payload;
};

const buildTripDetails = (scheduleResults: ScheduleResults, itinerary: Itinerary, tripDetailsForm: TripDetailsForm) => {
  const passengersCounts = {
    Adult: tripDetailsForm?.passengers.adults.length,
    Child: tripDetailsForm?.passengers.children.length,
    Military: 0,
    Senior: tripDetailsForm?.passengers.seniors.length,
    Student: 0
  };
  const tripDetails: { [key: string]: any } = {
    itinerary,
    passengersCounts,
    returnDate: '',
    tripDetailsForm,
    tripType: tripDetailsForm?.isRoundTrip ? 'Round Trip' : 'One Way'
  };

  const buildAddress = (cityData: StrapiCityDatum, stopUuid: string) => {
    if (cityData && cityData.attributes.stops) {
      const stop = cityData?.attributes.stops.data.find((stop) => {
        if (
          stop.attributes.partner_location_keys &&
          stop.attributes.partner_location_keys.data &&
          stop.attributes.partner_location_keys.data.length
        ) {
          return stop.attributes.partner_location_keys.data.some(
            (key) => key.attributes.partner_primary_key === stopUuid
          );
        }
        return false;
      });
      const data = stop?.attributes;
      if (data) {
        const address = [
          data.stop_address1,
          data.cities?.data[0].attributes.city_name,
          data.cities?.data[0].attributes.state?.data.attributes.state_id + ' ' + data.stop_postal_code
        ].join(', ');
        return {
          address,
          stopName: data.stop_name
        };
      }
    }

    return { address: '', stopName: '' };
  };

  if (scheduleResults && itinerary?.outbound && itinerary?.outbound.scheduleRun) {
    const departTime = itinerary.outbound.scheduleRun.departTime || '';
    const destination = itinerary.outbound.scheduleRun.destination;
    const origin = itinerary.outbound.scheduleRun.origin;
    tripDetails.departCity = origin.city.name;
    tripDetails.departState = origin.state.abbreviation;
    ({ address: tripDetails.departAddress, stopName: tripDetails.departStopName } = buildAddress(
      scheduleResults.originCityData,
      itinerary?.outbound.scheduleRun.origin.stopUuid
    ));
    tripDetails.arrivalCity = destination.city.name;
    tripDetails.arrivalState = destination.state.abbreviation;
    ({ address: tripDetails.arrivalAddress, stopName: tripDetails.arrivalStopName } = buildAddress(
      scheduleResults.destinationCityData,
      itinerary?.outbound.scheduleRun.destination.stopUuid
    ));
    tripDetails.departDate = formatDateString(departTime, origin.timeZone || '');
    tripDetails.fareType = itinerary.outbound.type;
    tripDetails.total = itinerary.outbound?.fare?.amount || '';
  }

  if (itinerary?.returning && itinerary.returning.scheduleRun) {
    const { departTime: departTimeReturn, origin: originReturn } = itinerary.returning.scheduleRun;
    tripDetails.returnDate = formatDateString(departTimeReturn, originReturn.timeZone);
    tripDetails.total = tripDetails.total + itinerary.returning?.fare?.amount;
  }

  return tripDetails;
};

const getFareByPassengerType = (
  itineraryItem: ItineraryItem,
  schedulesAllPassengerType: ScheduleProduct[] | undefined,
  passengerType: string
) => {
  let adultFare: any = itineraryItem.railgunFares
    ? itineraryItem.type === 'saver'
      ? itineraryItem.railgunFares.saver
      : itineraryItem.railgunFares.flex
    : null;
  if (!schedulesAllPassengerType || passengerType === 'Adult') {
    return adultFare;
  }
  // Find the selected schedule using itinerary data
  const schedule = schedulesAllPassengerType.filter((schedule) => {
    return (
      schedule.scheduleRun.scheduleUuid === itineraryItem.scheduleRun.scheduleUuid &&
      schedule.scheduleRun.origin.stopUuid === itineraryItem.scheduleRun.origin.stopUuid &&
      schedule.scheduleRun.destination.stopUuid === itineraryItem.scheduleRun.destination.stopUuid
    );
  })[0];
  // Get railgunFares for passenger type Senior/Child
  const fares = (schedule?.railgunFares as any)?.[passengerType];
  const fare =
    fares?.length &&
    fares[0].fares.find((fare: Fare) => fare.type === adultFare.type && fare.markup === adultFare.markup);
  return fare || adultFare;
};

const fetchAvailability = async (
  scheduleResults: ScheduleResults | null,
  itinerary: Itinerary,
  tripDetailsForm: TripDetailsForm,
  charges: Charges | null,
  protectionSelected: boolean | null,
  isUsingLoyalty: boolean,
  isUsingCredits: boolean,
  promoCode: string | null,
  reservationDetails?: ReservationDetails | null
) => {
  const isReissue = !!(
    reservationDetails &&
    (scheduleResults?.outboundReissueSchedules || scheduleResults?.returnReissueSchedules) &&
    itinerary.rescheduleLeg
  );
  const buildPassengers = (passengers: Passenger[], type: string) => {
    return passengers.map((passenger) => {
      const outboundFare: any = getFareByPassengerType(
        itinerary.outbound,
        scheduleResults?.outboundSchedulesAllPassengerType,
        type
      );
      const item: { [key: string]: any } = {
        firstName: passenger.firstName,
        lastName: passenger.lastName,
        wheelchair: passenger.wheel,
        type,
        outboundFare: {
          mileageFareId: outboundFare?.mileageFareId,
          specialFareId: outboundFare?.specialFareId,
          type: outboundFare?.type,
          markup: outboundFare?.markup
        },
        outboundBags: {
          count: 0
        }
      };
      if (tripDetailsForm.isRoundTrip && itinerary.returning) {
        const returnFare: any = getFareByPassengerType(
          itinerary.returning,
          scheduleResults?.returnSchedulesAllPassengerType,
          type
        );
        item.returningFare = {
          mileageFareId: returnFare?.mileageFareId,
          specialFareId: returnFare?.specialFareId,
          type: returnFare?.type,
          markup: returnFare?.markup
        };
        item.returningBags = {
          count: 0
        };
      }
      return item;
    });
  };
  const data: { [key: string]: any } = {
    outbound: {
      departDate: formatDateYearMonthDay(
        itinerary.outbound.scheduleRun.departTime,
        itinerary.outbound.scheduleRun.origin.timeZone
      ),
      destination: itinerary.outbound.scheduleRun.destination,
      occurrence: 1,
      origin: itinerary.outbound.scheduleRun.origin,
      scheduleUuid: itinerary.outbound.scheduleRun.scheduleUuid
    },
    ...(isReissue === false && {
      passengers: {
        Adult: buildPassengers(tripDetailsForm.passengers.adults, 'Adult'),
        Senior: buildPassengers(tripDetailsForm.passengers.seniors, 'Senior'),
        Child: buildPassengers(tripDetailsForm.passengers.children, 'Child')
      }
    }),
    purchaseType: 'SCHEDULE_BOOK',
    useLoyalty: isUsingLoyalty,
    useCredits: isUsingCredits,
    promoCode: promoCode
  };
  if (tripDetailsForm.isRoundTrip && itinerary.returning) {
    data.returning = {
      departDate: formatDateYearMonthDay(
        itinerary.returning.scheduleRun.departTime,
        itinerary.returning.scheduleRun.origin.timeZone
      ),
      destination: itinerary.returning.scheduleRun.destination,
      occurrence: 1,
      origin: itinerary.returning.scheduleRun.origin,
      scheduleUuid: itinerary.returning.scheduleRun.scheduleUuid
    };
  }
  if (config.enableProtectGroup && protectionSelected) {
    const protectionAddon = getProtectionAddon(charges?.addons);
    if (protectionAddon) {
      data.addonRequests = [{ feeId: protectionAddon.addonId, usages: 1 }];
    }
  }
  if (tripDetailsForm?.petCount) {
    const petAddon = getPetAddon(charges?.addons);
    if (petAddon) {
      const value = { feeId: petAddon.addonId, usages: tripDetailsForm.petCount };
      data.addonRequests = !data.addonRequests ? [value] : [...data.addonRequests, value];
    }
  }
  if (data.promoCode && scheduleResults) {
    data.promoCodeValidationData = {
      tripCarriers: uniq([
        ...getCarriers(itinerary.outbound)
          .map((carrier: { code: string; name: string } | undefined) => carrier?.code)
          .filter(Boolean),
        ...(itinerary.returning
          ? getCarriers(itinerary.returning)
              .map((carrier: { code: string; name: string } | undefined) => carrier?.code)
              .filter(Boolean)
          : [])
      ]),
      destinationCityId: scheduleResults.destinationCityData.attributes.city_id,
      originCityId: scheduleResults.originCityData.attributes.city_id,
      subtotal: charges?.subtotal || 0,
      total: charges?.total || 0
    };
  }
  if (isReissue) {
    data.purchaseType = 'REISSUE_BOOK';
    data.reissueRequest = buildReissueRequestPayload(itinerary, reservationDetails, tripDetailsForm);
    if (
      itinerary.rescheduleLeg === 'return' &&
      reservationDetails.directions.length === 1 &&
      reservationDetails.directions[0] === 'RETURNING'
    ) {
      data.outbound = data.returning;
      delete data.returning;
    }
  }
  try {
    return await getAvailability(data);
  } catch (error) {}
};

const buildReissueRequestPayload = (
  itinerary: Itinerary,
  reservationDetails: ReservationDetails,
  tripDetailsForm: TripDetailsForm | null,
  purchaseType?: string
) => {
  const passengerIds = tripDetailsForm?.passengers
    ? Object.values(tripDetailsForm.passengers).flatMap((group) => group.map((passenger) => passenger.passengerId))
    : [];
  const isReturnTrip = itinerary.rescheduleLeg === 'return';

  const getFareByPassenger = (
    itinerary: Itinerary,
    passenger: ReservationDetails['passengers'][0],
    fareType: string
  ) => {
    const passengerType = passenger.type;
    if (tripDetailsForm?.isRoundTrip && isReturnTrip && fareType === 'outbound') {
      return getRescheduleOriginalFare(passenger.outboundFare);
    }
    if (tripDetailsForm?.isRoundTrip && !isReturnTrip && fareType === 'return') {
      return getRescheduleOriginalFare(passenger.returnFare);
    }
    const schedule = isReturnTrip ? itinerary.returning : itinerary.outbound;
    if (schedule) {
      const fares = (schedule?.railgunFares as any)?.[passengerType];
      const fare =
        fares?.length &&
        fares[0].fares.find((fare: Fare) => fare.type === schedule.fare?.type && fare.markup === schedule.fare?.markup);
      return fare || schedule.fare;
    }
  };

  const payload = {
    orderConfirmationCode: reservationDetails.confirmationCode,
    passengers: reservationDetails.passengers
      .filter((passenger) => passengerIds.includes(passenger.passengerId))
      .map((passenger) => {
        const value: {
          directions: string[];
          fareInfo?: { newFare: Fare; originalFare: Fare };
          outboundFare: Fare;
          passenger: any;
          returningFare?: Fare;
        } = {
          directions: reservationDetails.directions,
          outboundFare: getFareByPassenger(itinerary, passenger, 'outbound'),
          ...(tripDetailsForm?.isRoundTrip && { returningFare: getFareByPassenger(itinerary, passenger, 'return') }),
          passenger: {
            passengerId: passenger.passengerId,
            outboundFare: passenger.outboundFare,
            ...(passenger.returnFare && { returnFare: passenger.returnFare }),
            type: passenger.type
          }
        };
        if (purchaseType === 'REISSUE_BOOK') {
          value.fareInfo = {
            newFare: getFareByPassenger(itinerary, passenger, isReturnTrip ? 'return' : 'outbound'),
            originalFare: getRescheduleOriginalFare(isReturnTrip ? passenger.returnFare : passenger.outboundFare)
          };
        }
        return value;
      })
  };
  if (
    tripDetailsForm?.isRoundTrip &&
    reservationDetails.directions.length === 1 &&
    reservationDetails.directions[0] === 'RETURNING'
  ) {
    return {
      ...payload,
      passengers: payload.passengers.map((passenger) => {
        return {
          directions: passenger.directions,
          outboundFare: passenger.returningFare,
          passenger: passenger.passenger
        };
      })
    };
  }
  return payload;
};

export const getLocations = async (includeNearbyStops?: boolean) => {
  let storageKey = 'locations';
  if (includeNearbyStops) {
    storageKey = 'locationsWithNearby';
  }
  const locations = getWithExpiry(storageKey);
  if (locations) {
    return Promise.resolve(locations);
  }
  const response = await getLocationsData(includeNearbyStops ? includeNearbyStops : false);
  setWithExpiry(response, 30 * 60 * 1000, storageKey);
  return response;
};

const setWithExpiry = (locations: StrapiCountries, ttl: number, storageKey: string) => {
  const items = {
    expiry: new Date().getTime() + ttl,
    locations: locations
  };
  localStorage.setItem(storageKey, JSON.stringify(items));
};

const getWithExpiry = (storageKey: string) => {
  const items = localStorage.getItem(storageKey);
  if (!items) {
    return null;
  }
  const { locations, expiry } = JSON.parse(items);
  if (new Date().getTime() > expiry) {
    localStorage.removeItem(storageKey);
    return null;
  }
  return locations;
};

const fetchCommuterPassAvailability = async (data: CommuterPassSelectForm) => {
  try {
    return await getAvailability({ purchaseType: 'COMMUTER_PASS_BOOK', ...data });
  } catch (error) {}
};

export const getPetAddon = (addons: Charges['addons'] | undefined) => {
  return addons?.find((addon: Charges['addons'][0]) => addon.name === config.PET_ADDON) || null;
};

export const getProtectionAddon = (addons: Charges['addons'] | undefined) => {
  return addons?.find((addon: Charges['addons'][0]) => addon.name === config.PROTECTION_ADDON) || null;
};
